/* eslint-disable camelcase */
import { useMemo, useState, useEffect, useCallback } from 'react';
import sortBy from 'lodash/sortBy';
import { useApolloClient } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';
import type { UseIssuesInput } from '@atlassian/jira-business-common';
import { shouldHandleTheError } from '@atlassian/jira-business-error-handling';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters';
import {
	getAGGCompliantData,
	useJqlPagination,
	useCacheActions,
	type AGGCompliantAPI,
	type ListConnection,
} from '@atlassian/jira-business-list-handlers';
import { markViewMetric, REQUEST_END, REQUEST_START } from '@atlassian/jira-business-performance';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { LOAD_MORE_EXPERIENCE, TIMELINE_PAGE_SIZE_ARGS } from '../../common/constants';
import { getVariables } from '../../common/utils/get-variables';
import { useRankTimelineIssue } from '../rank-issue';
import type {
	JWMTimelineViewItems,
	JWMTimelineViewItemsVariables,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges as ItemEdge,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node as ItemNode,
	JWMTimelineViewItems_jira,
} from './__generated_apollo__/JWMTimelineViewItems';
import { TIMELINE_VIEW_ITEMS_QUERY } from './gql';
import type { UpdateInCache, UpdateItemsInCache, TimelineViewIssue } from './types';
import { rankSorter, transformResult } from './utils';

const getIndexById = (list: ListConnection<ItemNode> | undefined, issueId: string) => {
	if (list == null || !Array.isArray(list?.edges)) {
		return -1;
	}

	return list.edges.findIndex(({ node }) => node?.issueId === Number(issueId));
};

const emptyArray: TimelineViewIssue[] = [];

export const useTimelineViewIssues = ({
	input,
	onComplete,
}: {
	input: UseIssuesInput;
	onComplete?: () => void;
}) => {
	const analyticsTaskName = 'useTimelineViewIssuesApolloCacheActions';
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();
	const { isHidingDoneItems } = useFilteredViewContextInput({
		view: 'timeline',
	});

	/**
	 * We only want to recompute variables when input is NOT deep equal
	 * We need to retain the referential equality of the queryVariable to prevent recreating some functions unnecessarily
	 *
	 * At the same time we want to give flexibility for the consumer to inline the object parameter
	 * when consuming this hook
	 */
	const queryVariables = useMemo(() => {
		const pageSize = input.parentIssue ? null : TIMELINE_PAGE_SIZE_ARGS;

		return {
			...getVariables({ ...input, isHidingDoneItems }),
			cloudId,
			...pageSize,
			withParentRank: ff('jwm-timeline-sort-parent-rank-first_waal4'),
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(input)]);

	const [hasLoadingStarted, setHasLoadingStarted] = useState(false);
	const client = useApolloClient();

	const { updateItemsInCache: updateItemsInCacheAction } = useCacheActions();

	const startExperience = useExperienceStart(LOAD_MORE_EXPERIENCE);
	const markExperienceSuccess = useExperienceSuccess(LOAD_MORE_EXPERIENCE);
	const markExperienceFailed = useExperienceFail(LOAD_MORE_EXPERIENCE);

	const { rankIssue } = useRankTimelineIssue({
		query: TIMELINE_VIEW_ITEMS_QUERY,
		queryVariables,
	});
	const onNextPageLoaded = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'timelineMoreItems loaded');
		markExperienceSuccess();
	}, [createAnalyticsEvent, markExperienceSuccess]);

	const onPaginationError = useCallback(
		(apolloError: ApolloError) => {
			if (!shouldHandleTheError(apolloError)) {
				return;
			}

			fireErrorAnalytics({
				meta: {
					id: 'timelineViewPaginationQuery',
					packageName: 'jiraBusinessTimeline',
					teamName: 'jira-yurree',
				},
				attributes: { message: 'Failed to fetch more issues in timeline view' },
				error: apolloError,
				sendToPrivacyUnsafeSplunk: true,
			});
			markExperienceFailed('get timeline view issues service', apolloError);
		},
		[markExperienceFailed],
	);

	const { data, loadFirstPage, loading, error, loadNextPage, isLoadingNextPage } = useJqlPagination<
		JWMTimelineViewItems,
		ItemNode,
		JWMTimelineViewItemsVariables
	>({
		variables: queryVariables,
		query: TIMELINE_VIEW_ITEMS_QUERY,
		// setting error policy to all here to deal with partial failures (https://hello.atlassian.net/wiki/spaces/JBus/pages/3148433025/RFC-080+Partial+data+apollo-client+and+JWM)
		errorPolicy: 'all',
		onCompleted: () => {
			markViewMetric('timeline', REQUEST_END);
			if (onComplete) onComplete();
		},
		onError: (apolloError) => {
			markViewMetric('timeline', REQUEST_END);

			if (!shouldHandleTheError(apolloError)) {
				return;
			}

			fireErrorAnalytics({
				meta: {
					id: 'timelineViewItemsQuery',
					packageName: 'jiraBusinessTimeline',
					teamName: 'jira-yurree',
				},
				attributes: { message: 'Failed to fetch issues in timeline view' },
				error: apolloError,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
		onPaginationError,
		onNextPageLoaded,
	});

	useEffect(() => {
		markViewMetric('timeline', REQUEST_START);
		loadFirstPage(); // fetch issues once on mount
		setHasLoadingStarted(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// TODO remove this function and reuse the useCacheActions https://jbusiness.atlassian.net/browse/JET-1548
	const updateInCache: UpdateInCache = useCallback(
		({ id, fields: { startDate, dueDate } }) => {
			const getCachedListData = () => {
				try {
					const read = client.readQuery({
						query: TIMELINE_VIEW_ITEMS_QUERY,
						variables: queryVariables,
					});

					fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloCacheRead taskSuccess', {
						task: analyticsTaskName,
					});

					return read;
				} catch (e) {
					fireErrorAnalytics({
						error: e instanceof Error ? e : new Error('Unknown cache read error'),
						meta: {
							id: 'updateInCache',
							packageName: 'jiraBusinessTimeline',
							teamName: 'jira-yurree',
						},
						sendToPrivacyUnsafeSplunk: true,
						skipSentry: true,
						attributes: {
							task: analyticsTaskName,
							query: JSON.stringify(TIMELINE_VIEW_ITEMS_QUERY),
							variables: JSON.stringify(queryVariables),
							eventName: 'apolloCacheRead taskFail',
							error: JSON.stringify(e),
						},
					});
					return null;
				}
			};
			const listData = getCachedListData();

			const _list = getAGGCompliantData<JWMTimelineViewItems, ItemNode>(
				listData ?? undefined,
				'list',
			);

			const index = getIndexById(_list ?? undefined, id);
			if (index === -1 || index == null) {
				return;
			}

			const edges = [...(_list?.edges || [])].map((edge) => {
				if (edge?.node?.issueId === Number(id)) {
					const { node } = edge;

					return {
						node: {
							...node,
							fields: (node?.fields || []).map((field) => {
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
								const newField: Record<string, any> = {};

								if (field != null && 'aliasFieldId' in field) {
									if (startDate != null && field?.aliasFieldId === 'startdate') {
										newField.date = startDate;
									}
								}

								if (field != null && 'fieldId' in field) {
									if (dueDate != null && field?.fieldId === 'duedate') {
										newField.date = dueDate;
									}
								}
								return { ...field, ...newField };
							}),
						},
					};
				}

				return edge;
			});
			try {
				client.writeQuery({
					query: TIMELINE_VIEW_ITEMS_QUERY,
					variables: queryVariables,
					data: {
						...listData,
						jira: {
							...listData?.jira,
							list: {
								...(listData?.jira?.list || {}),
								edges,
							},
						},
					},
				});
				fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloCacheWrite taskSuccess', {
					task: analyticsTaskName,
				});
			} catch (e) {
				fireErrorAnalytics({
					error: e instanceof Error ? e : new Error('Unknown cache write error'),
					meta: {
						id: 'updateInCache',
						packageName: 'jiraBusinessTimeline',
						teamName: 'jira-yurree',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
					attributes: {
						task: analyticsTaskName,
						query: JSON.stringify(TIMELINE_VIEW_ITEMS_QUERY),
						variables: JSON.stringify(queryVariables),
						eventName: 'apolloCacheWrite taskFail',
						error: JSON.stringify(e),
					},
				});
			}
		},
		[client, createAnalyticsEvent, queryVariables],
	);

	const updateItemsInCache: UpdateItemsInCache = useCallback(
		(updates) => {
			updateItemsInCacheAction<ItemNode, AGGCompliantAPI<JWMTimelineViewItems_jira>>({
				query: TIMELINE_VIEW_ITEMS_QUERY,
				variables: queryVariables,
				issueIds: updates.map((i) => i.id),
				updateItem: (existingIssue) => {
					const update = updates.find((i) => Number(i.id) === existingIssue.issueId);

					if (!update) return existingIssue;

					const {
						fields: { startDate, dueDate },
					} = update;

					const fields =
						existingIssue.fields?.map((field) => {
							if (field != null && 'aliasFieldId' in field) {
								if (startDate != null && field.aliasFieldId === 'startdate') {
									return { ...field, date: startDate };
								}
							}

							if (field != null && 'fieldId' in field) {
								if (dueDate != null && field.fieldId === 'duedate') {
									return { ...field, date: dueDate };
								}
							}

							return field;
						}) ?? null;

					return {
						...existingIssue,
						fields,
					};
				},
			});
		},
		[queryVariables, updateItemsInCacheAction],
	);

	const _data = getAGGCompliantData<JWMTimelineViewItems>(data, 'list');
	const edges = _data?.edges || emptyArray;
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const result = useMemo(() => transformResult(sortBy(edges as ItemEdge[], rankSorter)), [edges]);

	const loadNextPageWithExperienceMetrics = useCallback(() => {
		startExperience();
		loadNextPage();
	}, [loadNextPage, startExperience]);

	return {
		data: result,
		updateInCache,
		updateItemsInCache,
		// Make sure to return loading=true until items are loaded.
		loading: hasLoadingStarted ? loading : true,
		// return undefined to ignore so the UI won't try to show a flag
		error: shouldHandleTheError(error) ? error : undefined,
		loadNextPage: loadNextPageWithExperienceMetrics,
		isLoadingNextPage,
		rankIssue,
	};
};
